import React from 'react';
import { LockKeyhole, QrCode } from 'lucide-react';
import { Card } from '../components/ui/display/Card.jsx';
import { Button } from '../components/ui/actions/Button.jsx';
import { CountdownTimer } from '../components/widget/CountdownTimer.jsx';
import { formatBaht } from '../lib/price.js';
import { tFor } from '../i18n/i18n.js';
import { omiseSubmissionForNonce } from '../lib/omise.js';

let omiseScriptPromise;

export function loadOmiseScript(documentRef = document) {
  if (window.OmiseCard) return Promise.resolve(window.OmiseCard);
  if (omiseScriptPromise) return omiseScriptPromise;
  omiseScriptPromise = new Promise((resolve, reject) => {
    const existing = documentRef.querySelector('script[data-sanookpass-omise]');
    const script = existing || documentRef.createElement('script');
    const onLoad = () => window.OmiseCard ? resolve(window.OmiseCard) : reject(new Error('OMISE_UNAVAILABLE'));
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', () => reject(new Error('OMISE_LOAD_FAILED')), { once: true });
    if (!existing) {
      script.src = 'https://cdn.omise.co/omise.js';
      script.async = true;
      script.dataset.sanookpassOmise = 'true';
      documentRef.head.appendChild(script);
    }
  });
  return omiseScriptPromise;
}

export function StepOmisePay({
  publicKey,
  supportedMethods = [],
  nextAction,
  totalPrice,
  currency,
  expiresAt,
  submitting,
  errorMessage,
  onSubmitNonce,
  onExpired,
  onBack,
  locale,
}) {
  const t = tFor(locale);
  const [ready, setReady] = React.useState(Boolean(window.OmiseCard));
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    loadOmiseScript()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) setLoadError(t('pay.loadFailed')); });
    return () => { cancelled = true; };
  }, [t]);

  const openCheckout = () => {
    if (!ready || !publicKey || submitting) return;
    const hasCard = supportedMethods.includes('card');
    const hasPromptPay = supportedMethods.includes('promptpay');
    window.OmiseCard.configure({ publicKey });
    window.OmiseCard.open({
      amount: Math.round(Number(totalPrice) * 100),
      currency: String(currency || 'THB').toUpperCase(),
      locale,
      frameLabel: 'SanookPass',
      frameDescription: t('pay.omiseFrameDescription'),
      submitLabel: t('pay.cta'),
      defaultPaymentMethod: hasCard ? 'credit_card' : 'promptpay',
      otherPaymentMethods: hasCard && hasPromptPay ? 'promptpay' : '',
      onCreateTokenSuccess: (nonce) => onSubmitNonce(omiseSubmissionForNonce(nonce)),
    });
  };

  const qrAction = nextAction?.type === 'qr' ? nextAction : null;
  return (
    <Card title={t('pay.heading')}>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{t('pay.total')}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
          {formatBaht(totalPrice)} {currency}
        </span>
      </div>

      <aside className="sp-payment-trust" aria-label={t('pay.secureLabel')}>
        <div className="sp-payment-trust__headline">
          <span className="sp-payment-trust__lock" aria-hidden="true"><LockKeyhole size={15} /></span>
          <span><strong>{t('pay.secureLabel')}</strong><small>{t('pay.secureByOmise')}</small></span>
          <span className="sp-payment-trust__omise" aria-label="Omise">OMISE</span>
        </div>
        <div className="sp-payment-methods" aria-label={t('pay.paymentMethods')}>
          {supportedMethods.includes('card') && <span className="sp-payment-mark sp-payment-mark--visa">VISA</span>}
          {supportedMethods.includes('card') && <span className="sp-payment-mark sp-payment-mark--mastercard"><i /><i /></span>}
          {supportedMethods.includes('promptpay') && <span className="sp-payment-mark sp-payment-mark--promptpay">PromptPay</span>}
        </div>
      </aside>

      {expiresAt && (
        <CountdownTimer expiresAt={expiresAt} locale={locale} onExpire={onExpired} />
      )}

      {qrAction ? (
        <>
          <div className="sp-omise-qr">
            <div className="sp-omise-qr__title"><QrCode size={19} />{t('pay.promptPayTitle')}</div>
            <p>{t('pay.promptPayInstructions')}</p>
            <img src={qrAction.imageUrl} alt={t('pay.promptPayQrAlt')} />
            {qrAction.expiresAt && (
              <CountdownTimer
                expiresAt={qrAction.expiresAt}
                locale={locale}
                labelKey="pay.promptPayTimeRemaining"
                expiredKey="pay.promptPayExpired"
              />
            )}
            <span className="sp-omise-qr__waiting" role="status">
              <i aria-hidden="true" />{t('pay.promptPayWaiting')}
            </span>
          </div>
          <div className="sp-omise-qr__actions">
            <Button type="button" variant="secondary" onClick={onBack} disabled={submitting}>
              {t('pay.cancelAndChangeMethod')}
            </Button>
          </div>
          {errorMessage && <div className="sp-message is-error">{errorMessage}</div>}
        </>
      ) : (
        <>
          {(loadError || errorMessage) && <div className="sp-message is-error">{loadError || errorMessage}</div>}
          {!ready && !loadError && <div className="sp-payment-loading"><span className="sp-payment-loading__dot" />{t('pay.loadingMethods')}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 14 }}>
            <Button type="button" variant="secondary" onClick={onBack}>{t('pay.cancelAndChoose')}</Button>
            <Button type="button" onClick={openCheckout} disabled={!ready || !publicKey || submitting}>
              {submitting ? t('pay.processing') : t('pay.cta')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default StepOmisePay;
